"use client"

import { useState, useEffect, useRef } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { ChevronDown, Menu, X, Phone, Mail, MapPin, LogOut, User, Search, RotateCcw } from "lucide-react"
import "./Navbar.css"
import logo from "../../images/imperia-logo.png"

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [activeSubDropdown, setActiveSubDropdown] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [adminEmail, setAdminEmail] = useState("")
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [showLocationTooltip, setShowLocationTooltip] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0); // Tracks last scroll position
  const [visible, setVisible] = useState(true);
  const location = useLocation()
  const navigate = useNavigate()
  const dropdownTimeoutRef = useRef(null)
  const subDropdownTimeoutRef = useRef(null)

  useEffect(() => {
    if (window.innerWidth > 768) return;

    const handleScroll = () => {
      if (isMobileMenuOpen) return;
      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY) {
        setVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 8) {
        setVisible(false);
      }
      if (currentScrollY <= 0) {
        setVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isSearchOpen) {
      document.body.classList.add('search-open')
    } else {
      document.body.classList.remove('search-open')
    }
    if (window.innerWidth <= 768) {
      if (visible) {
        document.body.classList.remove('navbar-hidden');
      } else {
        document.body.classList.add('navbar-hidden');
      }
    }

    return () => {
      document.body.classList.remove('search-open')
      document.body.classList.remove('navbar-hidden');
    }
  }, [isSearchOpen, visible])

  useEffect(() => {
    const checkAuthStatus = () => {
      const isLoggedIn = localStorage.getItem("isAdminLoggedIn") === "true"
      if (isLoggedIn) {
        setIsAuthenticated(true)
        setAdminEmail("imperiahoses@gmail.com")
      } else {
        setIsAuthenticated(false)
        setAdminEmail("")
      }
    }
    checkAuthStatus()

    const handleStorageChange = (e) => {
      if (e.key === "isAdminLoggedIn") {
        checkAuthStatus()
      }
    }
    window.addEventListener("storage", handleStorageChange)

    const handleCustomLoginEvent = () => {
      checkAuthStatus()
    }
    window.addEventListener("adminLoginStatusChanged", handleCustomLoginEvent)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("adminLoginStatusChanged", handleCustomLoginEvent)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("isAdminLoggedIn")
      localStorage.removeItem("adminToken")
      localStorage.removeItem("adminEmail")
      localStorage.removeItem("tokenType")
      setIsAuthenticated(false)
      setAdminEmail("")
      window.dispatchEvent(new Event("adminLoginStatusChanged"))
      navigate("/")
      alert("Logged out successfully!")
    }
  }

  const hosesCategories = {
    "Assembly & Solution For Vehicle": [
      { name: "Tata", path: "/automobile-products" },
      { name: "Leyland", path: "/automobile-products" },
      { name: "Mahindra", path: "/automobile-products" },
      { name: "Eicher", path: "/automobile-products" },
      { name: "Man", path: "/automobile-products" },
      { name: "Mercedes", path: "/automobile-products" },
      { name: "Volvo", path: "/automobile-products" },
      { name: "Scania", path: "/automobile-products" },
      { name: "Renault", path: "/automobile-products" },
      { name: "Volvo", path: "/automobile-products" },
      { name: "Volkswagen", path: "/automobile-products" },
      { name: "Ford", path: "/automobile-products" },
      { name: "Toyota", path: "/automobile-products" },
      { name: "Iveco", path: "/automobile-productss" },
      { name: "DAF", path: "/automobile-products" },
      { name: "BMC", path: "/automobile-products" },
      { name: "Mitsubishi Otobus", path: "/automobile-products" },
    ],
    "Hoses & Tubes for Automobile": [
      { name: "General Purpose Hoses Coolant / Air / Fuel ", path: "/products/753383" },
      { name: "Hoses for Engine Oil ", path: "/products/1A9017" },
      { name: "Temperature Hoses ", path: "/products/4B1B48" },
      { name: "Air Brake Nylone Tube", path: "/products/D80250" },
      { name: "TPU tubes", path: "/products/0617B9" },
      { name: "LD tube ", path: "/products/AAAAF8" },
      { name: "PVC Linning tubes", path: "/products/B309C2" },
      { name: "Low pressure flexible wire braided ", path: "/products/B1EA75" },
    ],
    "Hoses for Medical Line": [
      { name: "Anti static Hose", path: "/products/6D6461" },
      { name: "High Pressure Oxygen Tube", path: "/products/ED0A70" },
      { name: "Transparent PVC tube ", path: "/products/AEDDE5" },
      { name: "Oxygen Hoses", path: "/products/EA8CD8" },
    ],
    "Hoses for Construction": [
      { name: "Water levelling PVC tube", path: "/products/17D599" },
      { name: "Garden Hose", path: "/products/E81D95" },
      { name: "High pressure water tube ", path: "/products/180EB1" },
      { name: "Rubber Hoses", path: "/products/D80089" },
      { name: "Washing Hoses", path: "/products/B97C17" },
      { name: "Spray Paint Hoses", path: "/products/743C3E" },
    ],
    "Hoses for Hardware & Industry": [
      { name: "TPU Coil", path: "/products/EA8E01" },
      { name: "TPU Rolls", path: "/products/3D4257" },
      { name: "Spray Hoses", path: "/products/D51AE8" },
      { name: "Temperature Hoses", path: "/products/4B1B48" },
      { name: "General Purpose Hoses Coolant / Air / Fuel", path: "/products/753383" },
      { name: "Cutting Oil Hoses", path: "/products/CF0F7A" },
      { name: "Welding Hose", path: "/products/F7DE3E" },
      { name: "Hydraulic Hose", path: "/products/78A9A5" },
      { name: "Low Pressure Hoses", path: "/products/9F317C" },
      { name: "Nylon High Temperature Tube", path: "/products/09506E" },
      { name: "Greese Gun Hoses", path: "/products/E8287F" },
    ],
    "TWO WHEELER SEGMENT": [
      { name: "Hydraulic Brake Hose", path: "/products/433CF3" },
      { name: "Side Guard", path: "/products/64F051" },
      { name: "Fuel Hose", path: "/products/EC62DC" },
    ],
  }

  const navigationItems = [
    { name: "Home", path: "/" },
    {
      name: "Company",
      path: "/company",
    },
    {
      name: (
        <>
          Products &<br />
          Solutions
        </>
      ),
      path: "/products",
      hasDropdown: true,
      dropdown: [
        { name: "FC-FCEV – Thermal Management", path: "/products/AAAAC6" },
        { name: "CNG Hose & Metal Tube", path: "/products/1B6047" },
        { name: "HVAC Lines & HVAC Assembly", path: "/products/1C2382" },
        { name: "Heater Hose / Coolant Pipes & Hoses", path: "/products/884EEA" },
        { name: "Brake Assembly & Clutch Assembly", path: "/products/1BDBDE" },
        { name: "Hydraulic Power Steering", path: "/products/B2998A" },
        { name: "Hydraulic Hose", path: "/products/78A9A5" },
        { name: "Structural / Fabrication Parts", path: "/products/658447" },
        { name: "High Pressure Tubes / Hydraulic Tube", path: "/products/5BECBF" },
        { name: "Aftertreatment System / Exhaust Assembly", path: "/products/A97D29" },
        { name: "Fuel Hose / Fuel Line Assembly", path: "/products/FE6B21" },
        { name: "Injector Leak off Assembly", path: "/products/6C05B4" },
        { name: "HP Injector (CRDI & GDI)", path: "/products/53E416" },
        { name: "Silicon Charge Air Cooler / Intercooler Assembly", path: "/products/FEF329" },
        { name: "Oil Line & Oil Cooler Taflon Assembly", path: "/products/9D0343" },
        { name: "Hydraulic Hose", path: "/products/385389" },
        { name: "Engine Tubular Dipstick Assemblies", path: "/products/E8EA88" },
        { name: "General Purpose Hose Air/ Water / Coolant / Fuel", path: "/products/00F2BF" },
        { name: "PVC Braided Tube", path: "/products/F4D0B5" },
        { name: "Trailer Coil & Pneumatic Hose", path: "/products/0AF3CE" },
        { name: "Fire Extinguisher Hoses", path: "/products/D2C7A8" },
        { name: "Spray Paint Machine Hoses", path: "/products/A8D085" },
        { name: "Washing Hoses", path: "/products/9B908D" },
        { name: "Two Wheeler Shelter", path: "/products/AFA9B3" },
        { name: "Two Wheeler Side Guard", path: "/products/C1D6B7" },
        { name: "Sadament Bowl", path: "/products/DAEE31" },
        { name: "Welding Hoses", path: "/products/F7DE3E" },
        { name: "Tyre Inflator Hoses", path: "/products/67AAB4" },
        { name: "Anti Static Hoses For Medical Industry", path: "/products/6D6461" },
      ],
    },
    {
      name: "Our Market",
      path: "/market",
    },
    {
      name: "Hoses",
      path: "/hoses",
      hasDropdown: true,
      isMegaMenu: true,
      megaMenuData: hosesCategories,
    },
    {
      name: (
        <>
          News &<br />
          Exhibitions
        </>
      ),
      path: "/news",
    },
    { name: "Contact", path: "/contact" },
  ]

  const handleDropdownEnter = (index) => {
    // Only apply timeout behavior for desktop (non-mobile menu)
    if (!isMobileMenuOpen) {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current)
      }
      setActiveDropdown(index)
      setActiveSubDropdown(null)
    }
  }

  const handleDropdownLeave = () => {
    // Only apply timeout behavior for desktop (non-mobile menu)
    if (!isMobileMenuOpen) {
      dropdownTimeoutRef.current = setTimeout(() => {
        setActiveDropdown(null)
        setActiveSubDropdown(null)
      }, 150)
    }
  }

  const handleSubDropdownEnter = (index) => {
    // Only apply timeout behavior for desktop (non-mobile menu)
    if (!isMobileMenuOpen) {
      if (subDropdownTimeoutRef.current) {
        clearTimeout(subDropdownTimeoutRef.current)
      }
      setActiveSubDropdown(index)
    }
  }

  const handleSubDropdownLeave = () => {
    // Only apply timeout behavior for desktop (non-mobile menu)
    if (!isMobileMenuOpen) {
      subDropdownTimeoutRef.current = setTimeout(() => {
        setActiveSubDropdown(null)
      }, 150)
    }
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setActiveDropdown(null)
    setActiveSubDropdown(null)
  }
  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);

    if (newState) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
  };
  const toggleMobileDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index)
    setActiveSubDropdown(null)
  }

  const toggleMobileSubDropdown = (index) => {
    setActiveSubDropdown(activeSubDropdown === index ? null : index)
  }

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`)
      setIsSearchOpen(false)
      setSearchQuery("")
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }
  const openGoogleMaps = () => {
    const latitude = 28.796718;
    const longitude = 77.077677;

    window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank");
  }

  return (
    <>
      {/* Top Bar */}
      <div className={`navbar-topbar ${!visible && window.innerWidth <= 768 ? 'hidden' : ''}`}>
        <div className="container">
          <div className="topbar-content">
            <div className="topbar-left">
              <a href="tel:+919350019570" className="contact-item">
                <Phone size={14} />
                <span>+91 9350019570</span>
              </a>
              <a href="mailto:imperiahoses@gmail.com" className="contact-item">
                <Mail size={14} />
                <span>imperiahoses@gmail.com</span>
              </a>
              <div
                className="contact-item"
                onClick={() => {
                  setShowLocationTooltip(true);
                  openGoogleMaps();
                  setTimeout(() => setShowLocationTooltip(false), 3000);
                }}
                style={{
                  cursor: "pointer",
                  position: "relative"
                }}
              >
                <MapPin size={14} />
                <span>Delhi, India</span>
                {showLocationTooltip && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    background: "white",
                    color: "#333",
                    padding: "5px 10px",
                    borderRadius: "4px",
                    fontSize: "0.8rem",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                    zIndex: 1002,
                    whiteSpace: "nowrap"
                  }}>
                    Delhi, India
                  </div>
                )}
              </div>
            </div>
            <div className="topbar-right">
              {isAuthenticated ? (
                <div className="admin-info">
                  <Link to="/admin/dashboard" className="admin-welcome" title="Go to Dashboard">
                    <User size={14} />
                    <span>Welcome, {adminEmail}</span>
                  </Link>
                  <button onClick={handleLogout} className="topbar-logout-btn" title="Logout">
                    <LogOut size={14} />
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <Link to="/admin/login" className="topbar-link">
                  <User size={14} />
                  <span>Admin</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`navbar ${isScrolled ? "scrolled" : ""} ${!visible && window.innerWidth <= 768 ? 'hidden' : ''}`}>
        <div className="container">
          <div className="navbar-container">
            <Link to="/" className="navbar-logo-wrapper" onClick={closeMobileMenu}>
              <img
                src={logo || "/placeholder.svg"}
                alt="Imperia Hoses Logo"
                style={{ width: "60px", height: "60px" }}
              />
              <div className="logo-text-group">
                <div className="logo-main-line">
                  <span className="logo-text">Imperia</span>
                  <span className="logo-accent">Hoses</span>
                </div>
                <span className="logo-tagline">Your Hoses Partner</span>
              </div>
            </Link>
            <div className={`navbar-menu ${isMobileMenuOpen ? "active" : ""}`}>
              {navigationItems.map((item, index) => (
                <div
                  key={index}
                  className={`navbar-item ${item.hasDropdown ? "has-dropdown" : ""} ${item.isMegaMenu ? "has-mega-menu" : ""}`}
                  onMouseEnter={() => item.hasDropdown && handleDropdownEnter(index)}
                  onMouseLeave={() => item.hasDropdown && handleDropdownLeave()}
                >
                  <div className="navbar-link-container">
                    <Link
                      to={item.path}
                      className={`navbar-link ${location.pathname === item.path ? "active" : ""}`}
                      onClick={closeMobileMenu}
                    >
                      {item.name}
                    </Link>
                    {item.hasDropdown && (
                      <button
                        className="mobile-dropdown-toggle"
                        onClick={() => toggleMobileDropdown(index)}
                        aria-label={`Toggle ${typeof item.name === "string" ? item.name : "menu"} menu`}
                      >
                        <ChevronDown className={`dropdown-icon ${activeDropdown === index ? "rotated" : ""}`} />
                      </button>
                    )}
                  </div>
                  {item.hasDropdown && (
                    <div
                      className={`dropdown-menu ${item.isMegaMenu ? "mega-menu" : ""} ${activeDropdown === index ? "active" : ""}`}
                      onMouseEnter={() => handleDropdownEnter(index)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      {item.isMegaMenu ? (
                        <div className="mega-menu-content">
                          {Object.entries(item.megaMenuData).map(([categoryName, items], categoryIndex) => (
                            <div key={categoryIndex} className="mega-menu-column">
                              <h4 className="mega-menu-heading">{categoryName}</h4>
                              <ul className="mega-menu-list">
                                {items.map((subItem, subIndex) => (
                                  <li key={subIndex}>
                                    <Link to={subItem.path} className="mega-menu-link" onClick={closeMobileMenu}>
                                      {subItem.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="dropdown-content">
                          {item.dropdown.map((dropdownItem, dropdownIndex) => (
                            <div
                              key={dropdownIndex}
                              className={`dropdown-item ${dropdownItem.hasSubDropdown ? "has-subdropdown" : ""}`}
                              onMouseEnter={() => dropdownItem.hasSubDropdown && handleSubDropdownEnter(dropdownIndex)}
                              onMouseLeave={() => dropdownItem.hasSubDropdown && handleSubDropdownLeave()}
                            >
                              <div className="dropdown-link-container">
                                <Link to={dropdownItem.path} style={{ marginBottom: "5px" }} className="mega-menu-link" onClick={closeMobileMenu}>
                                  {dropdownItem.name}
                                </Link>
                                {dropdownItem.hasSubDropdown && (
                                  <button
                                    className="mobile-subdropdown-toggle"
                                    onClick={() => toggleMobileSubDropdown(dropdownIndex)}
                                    aria-label={`Toggle ${dropdownItem.name} submenu`}
                                  >
                                    <ChevronDown
                                      className={`subdropdown-icon ${activeSubDropdown === dropdownIndex ? "rotated" : ""
                                        }`}
                                    />
                                  </button>
                                )}
                              </div>
                              {dropdownItem.hasSubDropdown && (
                                <div
                                  className={`subdropdown-menu ${activeSubDropdown === dropdownIndex ? "active" : ""}`}
                                  onMouseEnter={() => handleSubDropdownEnter(dropdownIndex)}
                                  onMouseLeave={handleSubDropdownLeave}
                                >
                                  <div className="subdropdown-content">
                                    {dropdownItem.subDropdown.map((subItem, subIndex) => (
                                      <Link
                                        key={subIndex}
                                        to={subItem.path}
                                        className="subdropdown-link"
                                        onClick={closeMobileMenu}
                                      >
                                        <span className="subdropdown-title">{subItem.name}</span>
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {/* "Get Quote" button for mobile menu */}
              {isMobileMenuOpen && (
                <div className="navbar-item">
                  <Link to="/contact" className="cta-btn cta-btn-mobile" onClick={closeMobileMenu}>
                    Get Quote
                  </Link>
                </div>
              )}
            </div>
            <div className="navbar-actions">
              <button
                className="search-btn"
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                aria-label="Search"
              >
                <Search size={20} />
              </button>
              {/* Hide desktop CTA button on mobile */}
              {!isMobileMenuOpen && (
                <Link to="/contact" className="cta-btn">
                  Get Quote
                </Link>
              )}
              <button
                className="mobile-menu-btn"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Search Bar */}
      <div className={`search-bar ${isSearchOpen ? "active" : ""} ${!visible && window.innerWidth <= 768 ? 'hidden' : ''}`}>        <div className="container">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search products by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="search-input"
          />
          <button
            className="search-submit"
            onClick={handleSearch}
            aria-label="Submit search"
          >
            <Search size={18} />
          </button>
          <button
            className="search-reset"
            onClick={() => setSearchQuery("")}
            disabled={!searchQuery}
            aria-label="Clear search"
          >
            <RotateCcw size={18} />
          </button>
          <button
            className="search-close"
            onClick={() => setIsSearchOpen(false)}
            aria-label="Close search"
          >
            <X size={20} />
          </button>
        </div>
      </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="mobile-overlay" onClick={closeMobileMenu}></div>}
    </>
  )
}

export default Navbar